import { Stack, Typography } from '@mui/material'
import { DataGrid as MuiDataGrid, DataGridProps as MuiDataGridProps } from '@mui/x-data-grid'
import { styled } from '@mui/material/styles'
import { DataGridPro as MuiDataGridPro, DataGridProProps as MuiDataGridProProps } from '@mui/x-data-grid-pro'
import { jaJP } from '@mui/x-data-grid-pro/locales'

interface CustomProps {
  noRowsText?: string
  border?: boolean
}

type CustomDataGridProProps = CustomProps & MuiDataGridProProps

const StyledDataGridPro = styled(MuiDataGridPro, {
  shouldForwardProp: (prop) => prop !== 'border',
})<{ border: boolean }>(({ border }) => ({
  // データがない場合の高さを上書き
  '--DataGrid-overlayHeight': '52px',
  // 縦方向の中央寄せ
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
  },
  // ヘッダ選択時のフォーカス枠の切れを調整
  '.MuiDataGrid-columnHeader:focus-within': {
    outlineOffset: -2,
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  border: border ? undefined : 'none',
  '& .MuiDataGrid-columnSeparator': {
    display: border ? undefined : 'none',
  },
}))

const StyledDataGrid = styled(MuiDataGrid, {
  shouldForwardProp: (prop) => prop !== 'border',
})<{ border: boolean }>(({ border }) => ({
  // データがない場合の高さを上書き
  '--DataGrid-overlayHeight': '52px',
  // 縦方向の中央寄せ
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
  },
  // ヘッダ選択時のフォーカス枠の切れを調整
  '.MuiDataGrid-columnHeader:focus-within': {
    outlineOffset: -2,
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  border: border ? undefined : 'none',
  '& .MuiDataGrid-columnSeparator': {
    display: border ? undefined : 'none',
  },
}))

export const DataGridPro: React.FC<CustomDataGridProProps> = (props) => {
  const { sx, slots = {}, noRowsText = 'データがありません', border = false, ...otherProps } = props
  return (
    <StyledDataGridPro
      border={border}
      slots={{
        ...slots,
        noRowsOverlay: () => <CustomNoRowsOverlay noRowsText={noRowsText} />,
        noResultsOverlay: () => <CustomNoRowsOverlay noRowsText={noRowsText} />,
      }}
      {...otherProps}
      hideFooterSelectedRowCount
      disableColumnMenu
      disableColumnReorder
      sortingOrder={['desc', 'asc']}
      localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
      sx={sx}
    />
  )
}

type CustomDataGridProps = CustomProps & MuiDataGridProps

export const DataGrid: React.FC<CustomDataGridProps> = (props) => {
  const { sx, slots = {}, noRowsText = 'データがありません', border = false, ...otherProps } = props
  return (
    <StyledDataGrid
      border={border}
      slots={{
        ...slots,
        noRowsOverlay: () => <CustomNoRowsOverlay noRowsText={noRowsText} />,
        noResultsOverlay: () => <CustomNoRowsOverlay noRowsText={noRowsText} />,
      }}
      {...otherProps}
      hideFooterSelectedRowCount
      disableColumnMenu
      sortingOrder={['desc', 'asc']}
      localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
      sx={sx}
    />
  )
}

const CustomNoRowsOverlay: React.FC<{ noRowsText: string }> = ({ noRowsText }) => (
  <Stack justifyContent="center" alignItems="left" height="100%" pl={2} minHeight={'52px'}>
    <Typography variant="body2">{noRowsText}</Typography>
  </Stack>
)
